// src/components/tasks/BlockTaskDialog.tsx
import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { TaskChat } from "./TaskChat"
import { SubtaskCreateDialog } from "./SubtaskCreateDialog"
import type { ExtendedTask } from "@/types/task"
import { db } from "@/lib/firebase"
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  doc,
  deleteDoc,
} from "firebase/firestore"
import { PlusCircle, X, MessageCircle, Trash2, MessageSquare } from "lucide-react"
import { toast } from "@/hooks/use-toast"
import { useAuthStore } from "@/store/authStore"
import { useNotificationStore } from "@/store/notificationStore"

// === ДИЗАЙН-СЛОЙ ===
import { blockTaskDialogTheme, BlockTaskHeader, SubtaskItem } from "./ui/BlockTaskDialogUI"

interface BlockTaskDialogProps {
  block: ExtendedTask | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onTaskUpdated: () => void
}

export function BlockTaskDialog({ 
  block, 
  open, 
  onOpenChange, 
  onTaskUpdated 
}: BlockTaskDialogProps) {

  const [addSubtaskOpen, setAddSubtaskOpen] = useState(false)
  const [subtasks, setSubtasks] = useState<ExtendedTask[]>([])
  const [selectedSubtaskId, setSelectedSubtaskId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { user } = useAuthStore()
  const { markTaskAsRead } = useNotificationStore()
  const isPavlik = Number(user?.id) === 4
  const userId = Number(user?.id) || 0

  // Подписка на подзадачи
  useEffect(() => {
    if (!block?.id || !open) return

    const subtasksQuery = query(
      collection(db, "tasks"),
      where("parentId", "==", block.id),
      orderBy("createdAt", "desc")
    )

    const unsubscribe = onSnapshot(subtasksQuery, (snapshot) => {
      const loaded = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as ExtendedTask[]

      setSubtasks(loaded)
    })

    return () => unsubscribe()
  }, [block?.id, open])

  const selectedSubtask = subtasks.find(sub => sub.id === selectedSubtaskId) || null

  // === КЛЮЧЕВОЙ useEffect: снимаем "Новую задачу" при открытии чата ===
  useEffect(() => {
    if (selectedSubtask?.id) {
      markTaskAsRead(selectedSubtask.id)
    }
  }, [selectedSubtask?.id, markTaskAsRead])

  const handleDeleteSubtask = async (subtaskId: string) => {
    if (!confirm("Удалить подзадачу навсегда?")) return

    setDeletingId(subtaskId)

    try {
      await deleteDoc(doc(db, "tasks", subtaskId))
      toast({ title: "Подзадача удалена" })
      onTaskUpdated()
    } catch (error: any) {
      console.error("Ошибка удаления:", error)
      toast({ title: "Ошибка", variant: "destructive" })
    } finally {
      setDeletingId(null)
    }
  }

  if (!block) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={blockTaskDialogTheme.dialogContent}>
        <DialogTitle className="sr-only">
          Блок задач: {block.title || "Без названия"}
        </DialogTitle>

        <div className={blockTaskDialogTheme.mainWrapper}>
          {/* Шапка */}
          <BlockTaskHeader>
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-3 rounded-full shadow-md">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white tracking-tight">
                  {block.title || "Без названия"}
                </h2>
                <p className="text-sm text-purple-200/90 mt-1">
                  Участники: {block.assignee || "—"}
                </p>
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="text-purple-300 hover:text-white hover:bg-purple-800/50 rounded-full transition-all"
            >
              <X className="h-6 w-6" />
            </Button>
          </BlockTaskHeader>

          <div className="relative flex flex-1 overflow-hidden">
            <div className={blockTaskDialogTheme.overlayGradient} />

            {/* Левая панель — список подзадач */}
            <div className={blockTaskDialogTheme.sidebar}>
              <Button
                onClick={() => setAddSubtaskOpen(true)}
                className={blockTaskDialogTheme.createSubtaskButton}
              >
                <PlusCircle className="h-6 w-6" />
                Создать подзадачу
              </Button>

              {subtasks.length > 0 ? (
                <div>
                  <p className="text-sm text-purple-300 mb-3 font-medium">
                    Подзадачи ({subtasks.length})
                  </p>
                  <div className="space-y-3">
                    {subtasks.map((sub) => {
                      const isNewSubtask = sub.unreadBy?.includes(userId) ?? false
                      const unreadCount = sub.unreadMessagesBy?.[userId] ?? 0

                      return (
                        <SubtaskItem
                          key={sub.id}
                          isSelected={selectedSubtaskId === sub.id}
                          isNewTask={isNewSubtask}
                          unreadMessages={unreadCount}
                          onClick={() => setSelectedSubtaskId(sub.id)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 truncate pr-8">{sub.title}</div>
                            {isPavlik && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleDeleteSubtask(sub.id) }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-full h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </SubtaskItem>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center text-purple-400 py-10">Пока нет подзадач</div>
              )}
            </div>

            {/* Чат */}
            <div className={blockTaskDialogTheme.chatContainer}>
              {selectedSubtask ? (
                <TaskChat task={selectedSubtask} />
              ) : (
                <div className={blockTaskDialogTheme.emptyChat}>
                  <MessageSquare className="h-16 w-16 text-purple-500/70" />
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Выберите подзадачу</h3>
                    <p className="text-lg">Кликните на подзадачу слева</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>

      <SubtaskCreateDialog
        open={addSubtaskOpen}
        onOpenChange={setAddSubtaskOpen}
        parentId={block.id!}
        onCreate={onTaskUpdated}
      />
    </Dialog>
  )
}